import type { MouseEvent, ReactNode, RefObject } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import {
    EventFeedCard,
    EventFeedContainer,
    EventFeedList,
    FeedEmptyState,
    FeedLoadMore,
} from './components';
import type { FeedEmptyStateProps } from './components';
import { useFeedPlayback } from './hooks';
import { useScrollToActiveCard } from './hooks/useScrollToActiveCard';
import { useInfiniteScroll } from '@/hooks';
import type { ActionMenuItem, CardMetadataItem } from '@/components';
import { EngineService } from '@/services/engine';
import { useSourcesStore } from '@/stores';
import { useEventFeedStore } from '@/stores/event-feed.store';
import type { Event } from '@/types/event';
import {
    usePipPlayerStore,
    type PipNavigationContext,
} from '@/stores/pip-player.store';
import { isMiddleClick, isOpenInNewTabClick } from '@/utils/mouse';
import { getEventTimeRange } from '@/utils/event-time-range';

const INFINITE_SCROLL_THRESHOLD_PX = 320;

interface FeedItemCardExtras {
    onRef?: (el: HTMLDivElement | null) => void;
    isWatching?: boolean;
    isPipWatching?: boolean;
    watchingLabel?: string;
    onQuickView?: (event: MouseEvent) => void;
}

interface WatchingOptions {
    isFocused?: boolean;
}

interface GenericEventFeedProps<T> {
    containerTitle: string;
    titleContent?: ReactNode;
    isLive?: boolean;
    isConnected?: boolean;
    showViewAll?: boolean;
    onViewAll?: () => void;
    publishesLayoutVisibility?: boolean;
    hasData?: boolean;
    items: T[];
    isLoading: boolean;
    emptyState?: FeedEmptyStateProps;
    itemKey: (item: T) => string;
    itemIsNew?: (item: T) => boolean;
    itemEvent: (item: T) => Event;
    cardTitle: string;
    itemDescription: (item: T) => string;
    itemSubtitle?: (item: T) => ReactNode;
    itemLicensePlate?: (item: T) => string | undefined;
    itemConfidence?: (item: T) => number | undefined;
    itemTimeMetadata?: (item: T) => CardMetadataItem | undefined;
    itemCameraName?: (item: T, event: Event) => string | undefined;
    itemExtras?: (item: T, event: Event) => FeedItemCardExtras;
    itemActionMenuItems?: (item: T, event: Event) => ActionMenuItem[];
    watchingOptions?: (item: T, event: Event) => WatchingOptions;
    itemSelectPayload?: (item: T) => unknown;
    onNavigate?: (item: T, clickEvent: MouseEvent<HTMLDivElement>) => void;
    onClick?: (item: T, clickEvent: MouseEvent<HTMLDivElement>) => void;
    useRelativeTime?: boolean;
    scrollContainerRef?: RefObject<HTMLDivElement | null>;
    onScroll?: () => void;
    quickViewContextType?: PipNavigationContext['type'];
    pagination?: {
        hasMore: boolean;
        isLoadingMore: boolean;
        onLoadMore: () => void;
        mode?: 'infinite' | 'loadMore';
    };
}

function buildTimeRange(event: Event) {
    const { startTime, endTime } = getEventTimeRange(event);
    if (startTime === undefined || endTime === undefined) return undefined;
    return { start: startTime, end: endTime };
}

function buildThumbnailUrl(event: Event) {
    if (event.thumbnailUrl) return event.thumbnailUrl;
    if (event.type === 'SCENE') {
        return EngineService.getSceneThumbnailUrl(event.id);
    }
    return EngineService.getEventThumbnailUrl(event.id, true);
}

export function GenericEventFeed<T>({
    containerTitle,
    titleContent,
    isLive,
    isConnected,
    showViewAll = true,
    onViewAll,
    publishesLayoutVisibility = true,
    hasData,
    items,
    isLoading,
    emptyState,
    itemKey,
    itemIsNew,
    itemEvent,
    cardTitle,
    itemDescription,
    itemSubtitle,
    itemLicensePlate,
    itemConfidence,
    itemTimeMetadata,
    itemCameraName,
    itemExtras,
    itemActionMenuItems,
    watchingOptions,
    itemSelectPayload,
    onNavigate,
    onClick,
    useRelativeTime = true,
    scrollContainerRef,
    onScroll,
    quickViewContextType,
    pagination,
}: GenericEventFeedProps<T>) {
    const getSourceById = useSourcesStore((state) => state.getSourceById);
    const addSources = useSourcesStore((state) => state.addSources);
    const reportDataStatus = useEventFeedStore(
        (state) => state.reportDataStatus,
    );
    const feedPlayingEventId = useEventFeedStore(
        (state) => state.feedPlayingEventId,
    );
    const { getWatchingState, buildClickHandler } = useFeedPlayback();

    const resolvedClick = useMemo(
        () =>
            onClick ??
            (onNavigate
                ? buildClickHandler(onNavigate, itemSelectPayload)
                : undefined),
        [buildClickHandler, itemSelectPayload, onClick, onNavigate],
    );
    const missingSourceIds = useMemo(() => {
        const uniqueSourceIds = new Set(
            items.map((item) => itemEvent(item).sourceId),
        );
        return Array.from(uniqueSourceIds).filter(
            (sourceId) => sourceId && !getSourceById(sourceId),
        );
    }, [items, itemEvent, getSourceById]);

    const internalScrollRef = useRef<HTMLDivElement>(null);
    const activeScrollRef = scrollContainerRef ?? internalScrollRef;

    const isInfiniteScroll = pagination?.mode !== 'loadMore';
    useInfiniteScroll(activeScrollRef, {
        enabled: !!pagination && isInfiniteScroll,
        hasMore: pagination?.hasMore ?? false,
        isLoading: pagination?.isLoadingMore ?? false,
        onLoadMore: pagination?.onLoadMore,
        threshold: INFINITE_SCROLL_THRESHOLD_PX,
    });

    const { setCardRef } = useScrollToActiveCard(
        feedPlayingEventId,
        isLoading,
        items.length,
        activeScrollRef,
    );

    const openPip = usePipPlayerStore((s) => s.open);
    const closePip = usePipPlayerStore((s) => s.close);
    const pipIsOpen = usePipPlayerStore((s) => s.isOpen);
    const pipMode = usePipPlayerStore((s) => s.mode);
    const pipEventId = usePipPlayerStore((s) => s.eventId);
    const refreshNavContext = usePipPlayerStore(
        (s) => s.refreshNavigationContext,
    );

    useEffect(() => {
        if (!publishesLayoutVisibility) return;
        if (isLoading) return;
        reportDataStatus(items.length > 0 ? 'loaded' : 'empty');
    }, [isLoading, items.length, publishesLayoutVisibility, reportDataStatus]);

    useEffect(() => {
        if (!quickViewContextType) return;
        refreshNavContext({
            type: quickViewContextType,
            items: items.map((item) => ({ id: itemEvent(item).id })),
            canLoadMore: pagination?.hasMore,
            loadMore: pagination?.onLoadMore,
        });
    }, [itemEvent, items, pagination, quickViewContextType, refreshNavContext]);

    useEffect(() => {
        if (missingSourceIds.length === 0) return;
        let cancelled = false;

        EngineService.getSourcesByIds(missingSourceIds)
            .then((sources) => {
                if (!cancelled && sources.length > 0) {
                    addSources(sources);
                }
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [missingSourceIds, addSources]);

    return (
        <EventFeedContainer
            title={containerTitle}
            titleContent={titleContent}
            isLive={isLive}
            isConnected={isConnected}
            isLoading={isLoading}
            showViewAll={showViewAll}
            onViewAll={onViewAll}
            hasData={hasData}
        >
            {!isLoading && items.length === 0 && emptyState ? (
                <FeedEmptyState {...emptyState} />
            ) : (
                <EventFeedList
                    scrollContainerRef={activeScrollRef}
                    onScroll={onScroll}
                    footer={
                        pagination && (
                            <FeedLoadMore
                                hasMore={pagination.hasMore}
                                isLoadingMore={pagination.isLoadingMore}
                                onLoadMore={pagination.onLoadMore}
                                auto={isInfiniteScroll}
                            />
                        )
                    }
                >
                    {items.map((item) => {
                        const event = itemEvent(item);
                        const source = getSourceById(event.sourceId);
                        const sourceType = event.sourceType ?? source?.type;
                        const cameraName =
                            itemCameraName?.(item, event) ??
                            event.sourceName ??
                            source?.displayName;
                        const extras = itemExtras?.(item, event);
                        const setActiveCardRef = setCardRef(event.id);
                        const setItemRef = (el: HTMLDivElement | null) => {
                            setActiveCardRef(el);
                            extras?.onRef?.(el);
                        };
                        const watchingOptionsResult = watchingOptions?.(
                            item,
                            event,
                        );
                        const watching = getWatchingState(event, {
                            ...watchingOptionsResult,
                        });

                        const isMainWatching =
                            extras?.isWatching ?? watching.isWatching ?? false;
                        const isPipWatching =
                            !!quickViewContextType &&
                            pipIsOpen &&
                            pipMode === 'event' &&
                            pipEventId === event.id;
                        const actionMenuItems = itemActionMenuItems?.(
                            item,
                            event,
                        );
                        const mergedExtras: FeedItemCardExtras = {
                            ...extras,
                            ...(quickViewContextType && {
                                isPipWatching,
                                onQuickView: isMainWatching
                                    ? undefined
                                    : (clickEvent: MouseEvent) => {
                                          clickEvent.stopPropagation();
                                          openPip({
                                              eventId: event.id,
                                              navigationContext: {
                                                  type: quickViewContextType,
                                                  items: items.map((i) => ({
                                                      id: itemEvent(i).id,
                                                  })),
                                                  currentIndex: items.findIndex(
                                                      (i) =>
                                                          itemEvent(i).id ===
                                                          event.id,
                                                  ),
                                                  canLoadMore:
                                                      pagination?.hasMore,
                                                  loadMore:
                                                      pagination?.onLoadMore,
                                              },
                                          });
                                      },
                            }),
                        };
                        return (
                            <EventFeedCard
                                key={itemKey(item)}
                                thumbnailUrl={buildThumbnailUrl(event)}
                                isScene={event.type === 'SCENE'}
                                title={itemSubtitle?.(item) ?? cardTitle}
                                description={itemDescription(item)}
                                licensePlate={itemLicensePlate?.(item)}
                                timestamp={event.createdAt}
                                timeRange={buildTimeRange(event)}
                                timeItem={itemTimeMetadata?.(item)}
                                cameraName={cameraName}
                                sourceType={sourceType}
                                confidence={itemConfidence?.(item)}
                                useRelativeTime={useRelativeTime}
                                onClick={
                                    resolvedClick
                                        ? (clickEvent) => {
                                              if (
                                                  isPipWatching &&
                                                  !isOpenInNewTabClick(
                                                      clickEvent,
                                                  )
                                              ) {
                                                  closePip();
                                              }
                                              resolvedClick(item, clickEvent);
                                          }
                                        : undefined
                                }
                                onAuxClick={
                                    resolvedClick
                                        ? (clickEvent) => {
                                              // Only the middle mouse button
                                              // opens the event in a new tab.
                                              if (!isMiddleClick(clickEvent))
                                                  return;
                                              resolvedClick(item, clickEvent);
                                          }
                                        : undefined
                                }
                                animated
                                isNew={itemIsNew?.(item) ?? false}
                                {...watching}
                                {...mergedExtras}
                                actionMenuItems={actionMenuItems}
                                onRef={setItemRef}
                            />
                        );
                    })}
                </EventFeedList>
            )}
        </EventFeedContainer>
    );
}
